/**
 * Year6ControlPanel — /year6/:slug/control
 *
 * Teacher remote. One tap: Start / Pause / Skip / Reset. Opens the
 * Mission Screen in a new tab. Adds a "Generate Highlight Reel"
 * button (reuses the prom pipeline backend).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { PlayCircle, PauseCircle, SkipForward, StopCircle, MonitorPlay, Copy, Sparkle, FilmReel, BookOpen, LockKey, Trophy, Share } from '@phosphor-icons/react';
import { LowContentRecoveryCard, AlmostReadyBoost } from '../components/Year6LowContentRecovery';
import { UploadsTodayPill, VersionUpdatesPill } from '../components/Year6NewContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 2000;

export default function Year6ControlPanel() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [state, setState] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [mbStatus, setMbStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const eventIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        setEvent(r.data);
        eventIdRef.current = r.data.event_id;
      } catch { /* ignore */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = async () => {
      if (!eventIdRef.current) { timer = setTimeout(tick, POLL_MS); return; }
      try {
        const [s, h, mb, st] = await Promise.all([
          axios.get(`${API}/events/${eventIdRef.current}/year6/mission-state`),
          axios.get(`${API}/events/${eventIdRef.current}/highlight/public-status`),
          axios.get(`${API}/events/${eventIdRef.current}/year6/memory-book/preview-status`),
          axios.get(`${API}/events/${eventIdRef.current}/year6/stats`),
        ]);
        if (!cancelled) { setState(s.data); setHighlight(h.data); setMbStatus(mb.data); setStats(st.data); }
      } catch { /* ignore */ }
      finally { if (!cancelled) timer = setTimeout(tick, POLL_MS); }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  const call = async (endpoint, successMsg) => {
    try {
      await axios.post(`${API}/events/${eventIdRef.current}/year6/${endpoint}`, {});
      if (successMsg) toast.success(successMsg);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  const screenUrl = typeof window !== 'undefined' ? `${window.location.origin}/year6/${slug}/screen` : '';
  const copyScreen = async () => {
    try { await navigator.clipboard.writeText(screenUrl); toast.success('Mission Screen URL copied'); }
    catch { toast.error('Could not copy'); }
  };

  const generateHighlight = async () => {
    try {
      await axios.post(`${API}/events/${eventIdRef.current}/prom-highlight/generate`, { trigger: 'year6_control' });
      toast.success('🎬 Generating highlight reel...');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start'); }
  };

  if (loading) {
    return <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center" data-testid="year6-control-loading"><Sparkle size={28} weight="fill" className="text-amber-300 animate-pulse" /></div>;
  }
  if (!event) {
    return <div className="min-h-screen bg-neutral-950 text-white p-6 text-center" data-testid="year6-control-error">Not found</div>;
  }

  const active = state?.active;
  const paused = state?.paused;
  const mission = state?.mission;

  const statusText = !active ? 'Not started' : paused ? 'Paused' : `Mission ${mission?.index || '?'}/${mission?.total || 30}`;
  const statusColour = !active ? 'text-white/70 bg-white/8 border-white/15' : paused ? 'text-amber-300 bg-amber-300/10 border-amber-300/25' : 'text-emerald-300 bg-emerald-300/10 border-emerald-300/25';

  return (
    <div className="min-h-screen bg-neutral-950 text-white p-5" data-testid="year6-control-panel">
      <div className="max-w-md mx-auto">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/40">🎓 Year 6 Control</p>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.16em] border ${statusColour}`} data-testid="year6-control-status">
            <Sparkle size={10} weight="fill" /> {statusText}
          </span>
        </div>
        <h1 className="text-2xl font-black mb-1" data-testid="year6-control-title">{event.event_name}</h1>
        <p className="text-white/55 text-sm mb-7">{event.school_name || 'Class page'}</p>

        {/* Low-content recovery / Almost-ready boost — strict if/else priority + hidden after purchase */}
        {stats && !stats.purchased && (
          stats.low_content
            ? <LowContentRecoveryCard slug={slug} />
            : (stats.almost_ready
              ? <AlmostReadyBoost slug={slug} testIdSuffix="control" />
              : null)
        )}

        {/* Live momentum — "X uploads today" — reinforces "this is working" */}
        {stats && <UploadsTodayPill count={stats.uploads_today} />}
        {/* Memory Book v2 — version-bump count — only after first parents have purchased */}
        {stats && <VersionUpdatesPill count={stats.version_updates_today} />}

        {/* Current mission preview */}
        {active && mission && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-4" data-testid="year6-control-current-mission">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-300 mb-1">Now showing</p>
            <p className="text-lg font-bold leading-tight mb-1">{mission.title}</p>
            <p className="text-white/45 text-[12px]">{state.seconds_remaining}s left · {mission.category}</p>
          </div>
        )}

        {/* PRIMARY — Start/Pause */}
        {!active ? (
          <button onClick={() => call('start-missions', '🎬 Missions started — open the screen')} className="w-full inline-flex items-center justify-center gap-3 px-6 py-5 rounded-2xl text-base font-black text-white shadow-2xl mb-3 transition-transform active:scale-[0.98]" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }} data-testid="year6-control-start">
            <PlayCircle size={22} weight="fill" /> Start Missions
          </button>
        ) : (
          <button onClick={() => call('pause-missions', paused ? '▶️ Resumed' : '⏸ Paused')} className="w-full inline-flex items-center justify-center gap-3 px-6 py-5 rounded-2xl text-base font-black text-white shadow-2xl mb-3 transition-transform active:scale-[0.98]" style={{ background: paused ? 'linear-gradient(135deg, #22C55E, #10B981)' : 'linear-gradient(135deg, #F59E0B, #F97316)' }} data-testid="year6-control-pause">
            {paused ? <><PlayCircle size={22} weight="fill" /> Resume</> : <><PauseCircle size={22} weight="fill" /> Pause</>}
          </button>
        )}

        {/* Quick actions */}
        <div className="space-y-3 mb-6">
          <button onClick={() => call('skip-mission', '⏭ Skipped to next mission')} disabled={!active} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white/85 border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed" data-testid="year6-control-skip">
            <SkipForward size={14} weight="bold" /> Skip to next mission
          </button>
          <button onClick={() => call('stop-missions', 'Stopped')} disabled={!active} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white/85 border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40 disabled:cursor-not-allowed" data-testid="year6-control-stop">
            <StopCircle size={14} weight="fill" /> Stop missions
          </button>
          <button onClick={() => window.open(screenUrl, '_blank')} className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white border border-amber-300/25 bg-amber-300/8 hover:bg-amber-300/12" data-testid="year6-control-open-screen">
            <MonitorPlay size={14} weight="fill" /> Open Mission Screen
          </button>
        </div>

        {/* Reveal moment — emotion peak */}
        <div className="rounded-xl p-4 mb-4 border" style={{ background: 'linear-gradient(135deg, rgba(250,204,21,0.10), rgba(245,158,11,0.05))', borderColor: 'rgba(250,204,21,0.25)' }} data-testid="year6-control-reveal-card">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200 mb-2 inline-flex items-center gap-1.5">
            <Trophy size={11} weight="fill" /> Final reveal moment
          </p>
          <p className="text-[12px] text-white/75 mb-3">Crown the winning team with a 5-second countdown + confetti on the projector.</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={async () => { try { await axios.post(`${API}/events/${eventIdRef.current}/year6/start-reveal`); toast.success('🏆 Reveal started — open the reveal screen'); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start'); } }}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-black text-black bg-amber-300 hover:bg-amber-400"
              data-testid="year6-control-start-reveal"
            >
              <Trophy size={12} weight="fill" /> Start reveal
            </button>
            <button
              onClick={() => window.open(`/year6/${slug}/reveal`, '_blank')}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-bold border border-white/15 bg-white/[0.04] hover:bg-white/[0.08]"
              data-testid="year6-control-open-reveal"
            >
              <MonitorPlay size={12} weight="fill" /> Open reveal screen
            </button>
          </div>
        </div>

        {/* Parent View deep-link — drives parent traffic to the conversion page */}
        <div className="rounded-xl p-4 mb-4 border border-white/10 bg-white/[0.03]" data-testid="year6-control-parent-view-card">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200 mb-2 inline-flex items-center gap-1.5">
            <Share size={11} weight="fill" /> Parent view link
          </p>
          <p className="text-[12px] text-white/75 mb-3">Send parents this curated, conversion-optimised page (not the upload UI).</p>
          <button
            onClick={async () => {
              const url = `${window.location.origin}/year6/${slug}/view`;
              try { await navigator.clipboard.writeText(url); toast.success('Parent view URL copied'); }
              catch { toast.error('Could not copy'); }
            }}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black text-black bg-white hover:bg-neutral-100"
            data-testid="year6-control-copy-parent-view"
          >
            <Copy size={12} weight="bold" /> Copy parent view URL
          </button>
        </div>

        {/* Awards & Certificates — Phase 2 keepsake editor */}
        <div className="rounded-xl p-4 mb-4 border" style={{ background: 'linear-gradient(135deg, rgba(229,194,107,0.12), rgba(159,117,23,0.04))', borderColor: 'rgba(229,194,107,0.30)' }} data-testid="year6-control-certs-card">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200 mb-2 inline-flex items-center gap-1.5">
            <Trophy size={11} weight="fill" /> Class awards &amp; certificates
          </p>
          <p className="text-[12px] text-white/75 mb-3">Review and personalise each pupil's award before generating the Memory Book. Auto-assigned — tweak as you like.</p>
          <button
            onClick={() => window.location.assign(`/year6/${slug}/certificates`)}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-black text-black bg-amber-300 hover:bg-amber-400"
            data-testid="year6-control-open-certs"
          >
            <Trophy size={12} weight="fill" /> Review certificates →
          </button>
        </div>

        {/* Projector URL */}
        <div className="rounded-xl bg-white/[0.03] border border-white/8 p-4 mb-4" data-testid="year6-control-projector">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/45 mb-2">Project this URL</p>
          <p className="text-[12px] text-white/85 break-words mb-3">{screenUrl}</p>
          <button onClick={copyScreen} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black text-black bg-white hover:bg-neutral-200" data-testid="year6-control-copy-screen-url">
            <Copy size={12} weight="bold" /> Copy Mission Screen URL
          </button>
        </div>

        {/* Memory Book unlock — surfaces when threshold met or once purchased */}
        {(mbStatus?.ready || mbStatus?.purchased) && (
          <div className="rounded-xl p-4 mb-4" style={{ background: mbStatus?.purchased ? 'linear-gradient(135deg, rgba(34,197,94,0.10), rgba(16,185,129,0.04))' : 'linear-gradient(135deg, rgba(250,204,21,0.10), rgba(245,158,11,0.05))', border: `1px solid ${mbStatus?.purchased ? 'rgba(34,197,94,0.25)' : 'rgba(250,204,21,0.30)'}` }} data-testid="year6-control-memory-book-card">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-200 mb-2 inline-flex items-center gap-1.5">
              <BookOpen size={11} weight="fill" /> {mbStatus?.purchased ? 'Memory Book — unlocked' : 'Memory Book is ready'}
            </p>
            {mbStatus?.purchased ? (
              <>
                <p className="text-[12px] text-white/75 mb-3">£99 unlocked. We'll email the PDF when it's rendered.</p>
                <button onClick={() => window.open(`/year6/${slug}/memory-book`, '_blank')} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black text-black bg-emerald-200 hover:bg-emerald-300" data-testid="year6-control-mb-open">
                  <BookOpen size={12} weight="fill" /> Open Memory Book page
                </button>
              </>
            ) : (
              <>
                <p className="text-[12px] text-white/75 mb-3">{mbStatus?.total} memories collected. Unlock the full class book.</p>
                <button onClick={() => window.open(`/year6/${slug}/memory-book`, '_blank')} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-black text-white" style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }} data-testid="year6-control-mb-unlock">
                  <LockKey size={12} weight="fill" /> Unlock Memory Book — £99
                </button>
              </>
            )}
          </div>
        )}

        {/* Highlight trigger */}
        <div className="rounded-xl bg-[#B89762]/30/8 border border-[#B89762]/40/20 p-4" data-testid="year6-control-highlight-card">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C29D52]/85 mb-2 inline-flex items-center gap-1.5">
            <FilmReel size={11} weight="fill" /> End-of-day highlight reel
          </p>
          {highlight?.status === 'ready' ? (
            <>
              <p className="text-[12px] text-white/75 mb-3">Your 30-second recap is ready.</p>
              <button onClick={() => window.open(`/event/${slug}?focus=highlight`, '_blank')} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black text-black bg-[#B89762]/20 hover:bg-[#B89762]/30" data-testid="year6-control-open-highlight">
                <FilmReel size={12} weight="fill" /> Open highlight ({highlight?.duration})
              </button>
            </>
          ) : highlight?.status === 'processing' ? (
            <>
              <p className="text-[12px] text-white/75 mb-2">Cooking highlights... {highlight?.progress || 0}%</p>
              <div className="w-full h-1.5 bg-white/8 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-[#B89762]/30 transition-all duration-1000" style={{ width: `${highlight?.progress || 0}%` }} />
              </div>
            </>
          ) : (
            <>
              <p className="text-[12px] text-white/75 mb-3">After the session, generate a 30-second recap the class can share.</p>
              <button onClick={generateHighlight} className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black text-black bg-[#B89762]/20 hover:bg-[#B89762]/30" data-testid="year6-control-generate-highlight">
                <FilmReel size={12} weight="fill" /> Generate highlights now
              </button>
              <p className="text-[11px] text-white/50 text-center mt-2">Needs at least 3 uploads</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
